import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { CustomDropdownContainer, FormFieldLabel, PageHeader } from "CommonComponent";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  FREQUENCY_OPTIONS,
  fetchGenerationLogs,
  fetchGenerationRuns,
  getErrorMessage,
} from "../pafEngineApi";
import "../PafEngine.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const PafEngineLogs = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [runs, setRuns] = useState([]);
  const [rows, setRows] = useState([]);
  const [frequency, setFrequency] = useState(null);
  const [result, setResult] = useState(null);
  const [employeeId, setEmployeeId] = useState("");
  const [runId, setRunId] = useState("");

  const load = useCallback(async () => {
    dispatch(showCommonLoader());
    try {
      const filters = {
        frequency: frequency?.value || undefined,
        result: result?.value || undefined,
        employee_id: employeeId || undefined,
        run_id: runId || undefined,
      };
      const [runList, logRows] = await Promise.all([
        fetchGenerationRuns(objectViewedId, {
          frequency: filters.frequency,
        }),
        fetchGenerationLogs(objectViewedId, filters),
      ]);
      setRuns(runList);
      setRows(logRows);
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId, frequency, result, employeeId, runId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="paf-settings-page">
      <PageHeader title="Generation Logs">
        <Button variant="outline-secondary" size="sm" onClick={load}>
          Refresh
        </Button>
      </PageHeader>

      <div className="main-content-div page-card">
        <Row className="g-3 mb-3">
          <Col md={3}>
            <FormFieldLabel>Frequency</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={FREQUENCY_OPTIONS}
              value={frequency}
              onChange={setFrequency}
              isClearable
              components={{ ValueContainer: CustomDropdownContainer }}
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Result</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={[
                { value: "eligible", label: "Eligible" },
                { value: "skipped", label: "Skipped" },
              ]}
              value={result}
              onChange={setResult}
              isClearable
              components={{ ValueContainer: CustomDropdownContainer }}
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Employee ID</FormFieldLabel>
            <Form.Control
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value.trim())}
              placeholder="UUID"
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Run ID</FormFieldLabel>
            <Form.Control
              value={runId}
              onChange={(e) => setRunId(e.target.value.trim())}
              placeholder="Optional run UUID"
            />
          </Col>
        </Row>

        <div className="paf-engine-section-title">Recent Runs</div>
        <Table responsive className="mb-4">
          <thead>
            <tr>
              <th>Time</th>
              <th>Trigger</th>
              <th>Mode</th>
              <th>Scanned</th>
              <th>Eligible</th>
              <th>Generated</th>
              <th>Skipped</th>
              <th>Duration</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td colSpan={9}>No runs yet.</td>
              </tr>
            ) : (
              runs.map((run) => (
                <tr key={run.id}>
                  <td>{String(run.date_created || "").replace("T", " ").slice(0, 19)}</td>
                  <td>{run.trigger}</td>
                  <td>{run.dry_run ? "Dry run" : "Live"}</td>
                  <td>{run.employees_scanned}</td>
                  <td>{run.eligible}</td>
                  <td>{run.generated}</td>
                  <td>{run.skipped}</td>
                  <td>{run.duration_ms} ms</td>
                  <td>{run.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <div className="paf-engine-section-title">Log Rows</div>
        <Table responsive>
          <thead>
            <tr>
              <th>Time</th>
              <th>Employee</th>
              <th>Appraisal Setup Name</th>
              <th>Policy</th>
              <th>Frequency</th>
              <th>Result</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7}>No log rows.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>{String(row.time || "").replace("T", " ").slice(0, 19)}</td>
                  <td>{row.employee_name || row.employee_id || "—"}</td>
                  <td>{row.setup_title || "—"}</td>
                  <td>{row.policy_name || "—"}</td>
                  <td>
                    {row.frequency
                      ? row.frequency.charAt(0).toUpperCase() + row.frequency.slice(1)
                      : "—"}
                  </td>
                  <td>{row.result}</td>
                  <td>{row.reason || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default PafEngineLogs;
